import type { FC } from 'react';

import { Placeholder, useCms, useContent, useRendering } from '@eneco/cms';
import { getStaticPrefix } from '@eneco/common/env';
import useDC from '@eneco/dc/useDC';
import { Card, Grid, Heading, IconList, Image, Stack, Text, TextLink } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { CheckIcon } from '@eneco/sparky/icons';

import type { MyEnvDisabledLandingPageCardContent, UspListItem } from './my-env-disabled-landing-page-card-content.types';
import { MY_ENV_DISABLED_LANDING_PAGE_CARD_SLOTS, resolveSlot } from './slots';

const hasLabel = (item: UspListItem): boolean => Boolean(item.label);

/**
 * `MyEnvDisabledLandingPageCard` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane (`libs/sitecore/components/content/src/MyEnvDisabledLandingPageCard/
 * MyEnvDisabledLandingPageCard.tsx`): the "MyEneco is unavailable, use the app" card, with a USP
 * checklist, an app-store QR code (desktop only) and a `StoreButtons` placeholder.
 *
 * `uspItemsList` resolves to unresolved references on real migrated content — items with no label are
 * dropped rather than rendered blank, the same trade `SimpleTable.tsx` makes for its own row gap. See
 * `my-env-disabled-landing-page-card-content.types.ts` for the field-group flattening this also handles.
 *
 * Known simplification: drops the legacy component's `TrackViewComponent` (`@eneco/tracking`) wrapper —
 * `lib:tracking` is not in `lib:cms-components`'s allowed dependencies, the same class of trade
 * `NotificationBox.tsx` documents for dropping its own view tracking.
 */
const MyEnvDisabledLandingPageCard: FC = () => {
  const { adapter } = useCms();
  const content = useContent<MyEnvDisabledLandingPageCardContent>();
  const rendering = useRendering();
  const { label } = useDC();
  const isMobile = !useMediaQuery('md');

  const { title, text, uspItemsList } = content.content ?? content;
  const { helpLink, helpText } = content.help ?? content;
  const { downloadAppTitle, downloadAppText, viewingFromAppText } = content.downloadApp ?? content;

  const link = adapter.toLink(helpLink);
  const uspItems = (uspItemsList ?? []).filter(hasLabel);
  const mainSlot = resolveSlot(rendering, MY_ENV_DISABLED_LANDING_PAGE_CARD_SLOTS.main);

  return (
    <Stack direction="row" alignX="center">
      <Grid justifyContent="center" gridTemplateColumns={isMobile ? '346px' : '392px'}>
        <Card>
          <Card.Content>
            <Stack gap={4}>
              <Heading as="h1" size="S">
                {title}
              </Heading>
              <Text as="p">{text}</Text>
              {uspItems.length > 0 && (
                <Stack.Item>
                  <IconList alignment="middle">
                    {uspItems.map((item, index) => (
                      <IconList.Item key={item.name ?? index} icon={<CheckIcon color="feedbackSuccess" />}>
                        {item.label}
                      </IconList.Item>
                    ))}
                  </IconList>
                </Stack.Item>
              )}

              <Stack gap={1} as="section">
                <Heading as="h2" size="3XS">
                  {downloadAppTitle}
                </Heading>
                {!isMobile && <Text as="p">{downloadAppText}</Text>}
              </Stack>

              {!isMobile && (
                <Stack alignX="center">
                  <Image
                    src={`${getStaticPrefix()}/images/${label}/app-store-qr-code.svg`}
                    alt="QR-code om de app op je telefoon te openen of te downloaden."
                    width="160px"
                    height="auto"
                    objectFit="fill"
                  />
                </Stack>
              )}
              {mainSlot && (
                <Stack alignX="center" gap={4}>
                  <Placeholder name={mainSlot} />
                </Stack>
              )}
              {isMobile && (
                <Text as="p" size="BodyS" color="textLowEmphasis">
                  {viewingFromAppText}
                </Text>
              )}
              {link?.href && (
                <Text as="p">
                  {helpText}&nbsp;
                  <TextLink href={link.href} target={link.target}>
                    {link.text ?? link.href}
                  </TextLink>
                </Text>
              )}
            </Stack>
          </Card.Content>
        </Card>
      </Grid>
    </Stack>
  );
};

export default MyEnvDisabledLandingPageCard;
